import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/Card'
import { Avatar } from '@/components/ui/Avatar'
import { Users, LogIn, UserPlus } from 'lucide-react'
import appIcon from '@/assets/images/icon-128.png'
import { AuthBackground } from '@/components/auth/AuthBackground'

interface Invitation {
  token: string
  email: string
  role: string
  account_name: string
  account_logo_url: string | null
  invited_by_name: string
  expires_at: string
}

interface AcceptProps {
  invitation: Invitation
  user_exists: boolean
  current_user_matches: boolean
  logged_in_as_different_user: boolean
}

export default function Accept({
  invitation,
  user_exists,
  current_user_matches,
  logged_in_as_different_user,
}: AcceptProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleAccept = () => {
    setIsSubmitting(true)
    router.post(
      `/invitations/${invitation.token}/accept`,
      {},
      {
        onFinish: () => setIsSubmitting(false),
      }
    )
  }

  const handleSignIn = () => {
    window.location.href = `/login?redirect_to=/invitations/${invitation.token}`
  }

  const handleSignUp = () => {
    // POST to start-signup endpoint to store invitation token in session
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/invitations/${invitation.token}/start-signup`

    if (csrfToken) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const handleSignOut = () => {
    router.delete('/logout', {
      onSuccess: () => {
        window.location.href = `/invitations/${invitation.token}`
      },
    })
  }

  return (
    <>
      <Head title={`Join ${invitation.account_name}`} />

      <AuthBackground>
        <div className="w-full max-w-md">
          <Card className="w-full">
            <CardHeader className="text-center">
              <img src={appIcon} alt="Banshee" className="size-16 mx-auto mb-4 rounded-xl" />
              <div className="flex justify-center mb-4">
                {invitation.account_logo_url ? (
                  <Avatar
                    name={invitation.account_name}
                    src={invitation.account_logo_url}
                    avatarType="custom"
                    size="xl"
                  />
                ) : (
                  <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="size-8 text-primary" />
                  </div>
                )}
              </div>
              <CardTitle>Join {invitation.account_name}</CardTitle>
              <CardDescription>
                {invitation.invited_by_name} has invited you to join their team as a{' '}
                <span className="font-medium">{invitation.role}</span>.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Invited email</span>
                  <span className="font-medium">{invitation.email}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Role</span>
                  <span className="font-medium capitalize">{invitation.role}</span>
                </div>
              </div>

              {/* User is logged in as the correct user */}
              {current_user_matches && (
                <Button
                  onClick={handleAccept}
                  className="w-full cursor-pointer"
                  disabled={isSubmitting}
                  size="lg"
                >
                  {isSubmitting ? 'Joining...' : 'Accept & Join Team'}
                </Button>
              )}

              {/* User is logged in as a different user */}
              {logged_in_as_different_user && (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-amber-500/10 text-sm text-amber-400">
                    You're signed in with a different email. Please sign out and sign in with{' '}
                    <span className="font-medium">{invitation.email}</span> to accept this invitation.
                  </div>
                  <Button
                    onClick={handleSignOut}
                    variant="outline"
                    className="w-full cursor-pointer"
                    size="lg"
                  >
                    <LogIn className="size-4 mr-2" />
                    Sign out & Continue
                  </Button>
                </div>
              )}

              {/* User is not logged in */}
              {!current_user_matches && !logged_in_as_different_user && (
                <div className="space-y-3">
                  {user_exists ? (
                    <>
                      <p className="text-sm text-center text-muted-foreground">
                        Sign in to accept this invitation
                      </p>
                      <Button
                        onClick={handleSignIn}
                        className="w-full cursor-pointer"
                        size="lg"
                      >
                        <LogIn className="size-4 mr-2" />
                        Sign In
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-center text-muted-foreground">
                        Create an account to join the team
                      </p>
                      <Button
                        onClick={handleSignUp}
                        className="w-full cursor-pointer"
                        size="lg"
                      >
                        <UserPlus className="size-4 mr-2" />
                        Create Account
                      </Button>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-card px-2 text-muted-foreground">Or</span>
                        </div>
                      </div>
                      <Button
                        onClick={handleSignIn}
                        variant="outline"
                        className="w-full cursor-pointer"
                      >
                        <LogIn className="size-4 mr-2" />
                        Sign in with existing account
                      </Button>
                    </>
                  )}
                </div>
              )}
            </CardContent>

            <CardFooter className="justify-center">
              <p className="text-xs text-muted-foreground text-center">
                By accepting, you agree to join {invitation.account_name}'s workspace on Banshee.chat.
              </p>
            </CardFooter>
          </Card>
        </div>
      </AuthBackground>
    </>
  )
}
